import { useState, useEffect } from 'react';
import type { Tenant, TenantFormData, TenantType, SchoolType, AdminInfo, PlatformUser } from '../types';
import { mockDatacenters, mockPlatformUsers } from '../mockData';

interface TenantFormModalProps {
  tenant: Tenant | null;
  onClose: () => void;
  onSave: (data: TenantFormData) => void;
}

export default function TenantFormModal({ tenant, onClose, onSave }: TenantFormModalProps) {
  const [formData, setFormData] = useState<TenantFormData>({
    type: 'enterprise',
    status: 'active',
    adminPhone: '',
    datacenter: '',
    datacenters: [],
    contact: '',
    contactPhone: '',
    remark: '',
    enterpriseInfo: {
      license: null,
      name: '',
      creditCode: '',
      legalPersonName: '',
      legalPersonIdCard: '',
      businessAddress: ''
    },
    schoolInfo: {
      name: '',
      province: '',
      city: '',
      district: '',
      detailAddress: '',
      type: 'university'
    }
  });

  const [adminSearch, setAdminSearch] = useState('');
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<PlatformUser | null>(null);

  useEffect(() => {
    if (tenant) {
      setFormData({
        type: tenant.type,
        status: tenant.status,
        adminInfo: tenant.adminInfo,
        adminPhone: tenant.adminInfo.phone,
        datacenter: tenant.datacenter,
        datacenters: tenant.datacenters || [],
        contact: tenant.contact,
        contactPhone: tenant.contactPhone,
        remark: tenant.remark,
        enterpriseInfo: tenant.enterpriseInfo || {
          license: null,
          name: '',
          creditCode: '',
          legalPersonName: '',
          legalPersonIdCard: '',
          businessAddress: ''
        },
        schoolInfo: tenant.schoolInfo || {
          name: '',
          province: '',
          city: '',
          district: '',
          detailAddress: '',
          type: 'university'
        }
      });
      
      // 设置已选择的管理员信息
      if (tenant.adminInfo) {
        const adminUser = mockPlatformUsers.find(user => user.userId === tenant.adminInfo.userId);
        if (adminUser) {
          setSelectedAdmin(adminUser);
          setAdminSearch('');
        }
      }
    }
  }, [tenant]);

  // 过滤平台用户，支持手机号、姓名、用户ID搜索
  const filteredUsers = mockPlatformUsers.filter(user =>
    user.phone.includes(adminSearch) || 
    user.name.includes(adminSearch) || 
    user.userId.toLowerCase().includes(adminSearch.toLowerCase())
  );

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        enterpriseInfo: {
          ...formData.enterpriseInfo!,
          license: {
            fileUrl: URL.createObjectURL(file),
            fileName: file.name
          }
        }
      });
    }
  };

  const handleAdminSelect = (user: PlatformUser) => {
    setSelectedAdmin(user);
    setFormData({
      ...formData,
      adminInfo: {
        userId: user.userId,
        username: user.username,
        name: user.name,
        phone: user.phone
      },
      adminPhone: user.phone
    });
    setAdminSearch('');
    setShowAdminDropdown(false);
  };

  const handleAdminChange = () => {
    setSelectedAdmin(null);
    setFormData({
      ...formData,
      adminInfo: undefined,
      adminPhone: ''
    });
    setAdminSearch('');
    setShowAdminDropdown(true);
  };

  const handleDatacenterToggle = (datacenter: string) => {
    const newDatacenters = formData.datacenters.includes(datacenter)
      ? formData.datacenters.filter(dc => dc !== datacenter)
      : [...formData.datacenters, datacenter];
    
    setFormData({
      ...formData,
      datacenters: newDatacenters,
      datacenter: newDatacenters[0] || ''
    });
  };

  const handleSubmit = () => {
    if (formData.type === 'enterprise') {
      if (!formData.enterpriseInfo?.name || !formData.enterpriseInfo?.creditCode) {
        alert('请填写完整的企业信息');
        return;
      }
    } else {
      if (!formData.schoolInfo?.name || !formData.schoolInfo?.province) {
        alert('请填写完整的学校信息');
        return;
      }
    }

    if (!formData.adminInfo || formData.datacenters.length === 0 || !formData.contact || !formData.contactPhone) {
      alert('请填写完整的基本信息');
      return;
    }

    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{tenant ? '编辑租户' : '新建租户'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              租户类型 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="enterprise"
                  checked={formData.type === 'enterprise'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as TenantType })}
                  className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700">企业</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="school"
                  checked={formData.type === 'school'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as TenantType })}
                  className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700">学校</span>
              </label>
            </div>
          </div>

          {formData.type === 'enterprise' && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">企业信息</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">企业执照</label>
                  {formData.enterpriseInfo?.license ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{formData.enterpriseInfo.license.fileName}</span>
                      <button
                        onClick={() => setFormData({
                          ...formData,
                          enterpriseInfo: { ...formData.enterpriseInfo!, license: null }
                        })}
                        className="text-red-600 hover:text-red-700"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    企业名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.enterpriseInfo?.name || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      enterpriseInfo: { ...formData.enterpriseInfo!, name: e.target.value }
                    })}
                    placeholder="请输入企业名称"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    统一社会信用代码 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.enterpriseInfo?.creditCode || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      enterpriseInfo: { ...formData.enterpriseInfo!, creditCode: e.target.value }
                    })}
                    placeholder="请输入统一社会信用代码"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">法人姓名</label>
                  <input
                    type="text"
                    value={formData.enterpriseInfo?.legalPersonName || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      enterpriseInfo: { ...formData.enterpriseInfo!, legalPersonName: e.target.value }
                    })}
                    placeholder="请输入法人姓名"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">法人身份证号码</label>
                  <input
                    type="text"
                    value={formData.enterpriseInfo?.legalPersonIdCard || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      enterpriseInfo: { ...formData.enterpriseInfo!, legalPersonIdCard: e.target.value }
                    })}
                    placeholder="请输入法人身份证号码"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">营业场所/住所</label>
                  <input
                    type="text"
                    value={formData.enterpriseInfo?.businessAddress || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      enterpriseInfo: { ...formData.enterpriseInfo!, businessAddress: e.target.value }
                    })}
                    placeholder="请输入营业场所/住所"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>
          )}

          {formData.type === 'school' && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">学校信息</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    学校名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolInfo?.name || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      schoolInfo: { ...formData.schoolInfo!, name: e.target.value }
                    })}
                    placeholder="请输入学校名称"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    学校类型 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.schoolInfo?.type || 'university'}
                    onChange={(e) => setFormData({
                      ...formData,
                      schoolInfo: { ...formData.schoolInfo!, type: e.target.value as SchoolType }
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="primary">小学</option>
                    <option value="middle">初中</option>
                    <option value="vocational">职高</option>
                    <option value="high">高中</option>
                    <option value="college">大专</option>
                    <option value="university">大学</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    省份 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolInfo?.province || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      schoolInfo: { ...formData.schoolInfo!, province: e.target.value }
                    })}
                    placeholder="请输入省份"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    城市 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolInfo?.city || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      schoolInfo: { ...formData.schoolInfo!, city: e.target.value }
                    })}
                    placeholder="请输入城市"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    区县 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolInfo?.district || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      schoolInfo: { ...formData.schoolInfo!, district: e.target.value }
                    })}
                    placeholder="请输入区县"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    详细地址 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.schoolInfo?.detailAddress || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      schoolInfo: { ...formData.schoolInfo!, detailAddress: e.target.value }
                    })}
                    placeholder="请输入详细地址"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">租户状态</label>
              <button
                onClick={() => setFormData({ ...formData, status: formData.status === 'active' ? 'disabled' : 'active' })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                  formData.status === 'active' ? 'bg-teal-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                租户管理员 <span className="text-red-500">*</span>
              </label>
              
              {selectedAdmin ? (
                <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900">{selectedAdmin.name}</div>
                    <div className="text-gray-500">ID: {selectedAdmin.userId} | 手机: {selectedAdmin.phone}</div>
                  </div>
                  <button
                    onClick={handleAdminChange}
                    className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
                  >
                    更改
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={adminSearch}
                    onChange={(e) => {
                      setAdminSearch(e.target.value);
                      setShowAdminDropdown(true);
                    }}
                    onFocus={() => setShowAdminDropdown(true)}
                    placeholder="请输入手机号/姓名/用户ID搜索用户"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {showAdminDropdown && adminSearch && filteredUsers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredUsers.map((user) => (
                        <div
                          key={user.userId}
                          onClick={() => handleAdminSelect(user)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-xs text-gray-500">
                            ID: {user.userId} | 手机: {user.phone} | 部门: {user.department}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showAdminDropdown && adminSearch && filteredUsers.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
                      <div className="text-sm text-gray-500 text-center">未找到匹配的用户</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                关联机房 <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {mockDatacenters.map((dc) => (
                  <label
                    key={dc}
                    className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer transition-colors ${
                      formData.datacenters.includes(dc)
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-300 hover:border-teal-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.datacenters.includes(dc)}
                      onChange={() => handleDatacenterToggle(dc)}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">{dc}</span>
                  </label>
                ))}
              </div>
              {formData.datacenters.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  已选择 {formData.datacenters.length} 个机房
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                联系人 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder="请输入联系人"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                联系电话 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                placeholder="请输入联系电话"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
              <input
                type="text"
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                placeholder="请输入备注"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
